/**
 * Client Portal — Request a Service modal.
 *
 * Existing clients use the intake flow (subset of steps) to request a new or additional
 * therapy service while skipping screening questions, demographics, and email verification.
 * Group Therapy adds a cohort selection step and may book a screening call.
 */

import { Modal } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { resolveIntakeOrganizationIdFromPatient } from '@/lib/resolveIntakeOrganizationFromPatient';
import { useEffectivePortalPatientId } from '../../../hooks/usePortalSubjectPatientId';
import { IntakeFlow } from '../../public/intake/IntakeFlow';
import type { TherapyType } from '../../public/intake/types';
import '../../public/intake/intake.css';
import { fetchActiveEpisodeTherapyTypeCodes } from '../utils/portalEoc';

interface RequestServiceModalProps {
  opened: boolean;
  onClose: () => void;
}

export function RequestServiceModal({ opened, onClose }: RequestServiceModalProps): JSX.Element | null {
  const medplum = useMedplum();
  const patientId = useEffectivePortalPatientId();
  const [activeStep, setActiveStep] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const [intakeOrganizationId, setIntakeOrganizationId] = useState<string | undefined>();
  const [excludedTypeCodes, setExcludedTypeCodes] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!opened) {
      setActiveStep(0);
      setIsCompleting(false);
      setExcludedTypeCodes(new Set());
      setIntakeOrganizationId(undefined);
      return;
    }

    if (!patientId) {
      setExcludedTypeCodes(new Set());
      setIntakeOrganizationId(undefined);
      return;
    }

    let cancelled = false;
    void fetchActiveEpisodeTherapyTypeCodes(medplum, patientId).then((codes) => {
      if (!cancelled) setExcludedTypeCodes(codes);
    });
    void resolveIntakeOrganizationIdFromPatient(medplum, patientId)
      .then((orgId) => {
        if (!cancelled) setIntakeOrganizationId(orgId);
      })
      .catch(() => {
        if (!cancelled) setIntakeOrganizationId(undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [opened, patientId, medplum]);

  const excludedTherapyTypes = useMemo(
    () => [...excludedTypeCodes] as TherapyType[],
    [excludedTypeCodes],
  );

  const handleClose = useCallback((): void => {
    onClose();
  }, [onClose]);

  const handleComplete = useCallback((): void => {
    notifications.show({
      title: 'Request submitted',
      message: 'Your service request has been received.',
      color: 'green',
    });
    onClose();
  }, [onClose]);

  const isWide = activeStep === 9 || activeStep === 10;

  if (!opened) return null;

  return (
    <Modal
      opened
      onClose={handleClose}
      size={isCompleting ? 'md' : 'xl'}
      centered={isCompleting}
      classNames={{
        content: `intake-modal-content${isWide ? ' intake-modal-content--results' : ''}${
          isCompleting ? ' intake-modal-content--complete' : ''
        }`,
        body: 'intake-modal-body intake-modal-body--flush',
        header: 'intake-modal-header-hidden',
      }}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={!isCompleting}
    >
      <IntakeFlow
        billingPatientId={patientId}
        intakeOrganizationId={intakeOrganizationId}
        flowVariant="request-service"
        excludedTherapyTypes={excludedTherapyTypes}
        onClose={handleClose}
        showCloseButton
        inModal
        onStepChange={setActiveStep}
        onCompletingChange={setIsCompleting}
        onComplete={handleComplete}
        completionCloseLabel="Closing…"
      />
    </Modal>
  );
}
